/* línkè · 功能开关（feature flags）中枢 =============================
   每个新 / 有风险的功能挂一个开关，默认关。坏了一关就回退，不动主程序。
   存 localStorage（lingke.flags.<id>）。注意：flags **不在** sync.js 的
   SYNCED_KEYS 同步名单里 = 每台设备各自设置、不跨设备同步（设备相关的
   开关如"减弱动效"本就该如此）。要改成同步的话去 sync.js 加名单+前缀支持。

   这是路线图任务 ①「开关系统」——后续 ②斜杠命令 / ③Bark / ④活动窗 /
   ⑤思考卡片 都来这里注册自己的开关。
   ================================================================ */
import { useState, useEffect } from 'react';

const PREFIX = 'lingke.flags.';

/* 开关注册表。status:
     'ready' = 已实现可用
     'soon'  = 占位（对应功能还没做，面板里显示「敬请期待」、不可开） */
export const FLAGS = [
  {
    id: 'reduce_motion',
    label: '减弱动效',
    desc: '关掉星空闪烁和过渡动画，省电、更安静。',
    status: 'ready',
    default: false,
  },
  {
    id: 'slash_commands',
    label: '斜杠命令',
    desc: '在输入框打 /new 开新对话、/list 打开对话列表。',
    status: 'ready',
    default: false,
  },
  {
    id: 'bark_push',
    label: '手机推送',
    desc: 'Sael 回复时、你不在页面（切走/锁屏）就推一条到手机（需装 Bark）。',
    status: 'ready',
    default: false,
  },
  {
    id: 'activity_window',
    label: '活动实时窗',
    desc: '右上角开个终端风小窗，实时看Sael在调哪个记忆工具、结果如何。',
    status: 'ready',
    default: false,
  },
  {
    id: 'thinking_card',
    label: '显示思考卡片',
    desc: '深度模式下，回复上方那张可展开的「Sael的思考」卡片。想要纯净对话就关掉它。',
    status: 'ready',
    default: true,
  },
  {
    id: 'hide_avatar',
    label: '隐藏头像',
    desc: '聊天里藏起两边的头像（他的星星 ✦ / 你的月亮 ☾），气泡自动贴边、更清爽。',
    status: 'ready',
    default: false,
  },
  {
    id: 'accent_shades',
    label: '强调色·深浅联动',
    desc: '从你选的强调色自动配出亮一档和暗一档。关掉=深浅都用同一个颜色，最素。',
    status: 'ready',
    // 铃 2026-07-30：她平时都关着，可 flag 是设备本地、不进云同步，每次清缓存/新设备
    // 就回到默认「开」，害她反复手动关。改成默认「关」＝素色，想要深浅联动再自己开。
    default: false,
  },
  {
    id: 'crab_pet',
    label: 'Clawd 小螃蟹',
    desc: '官方 Clawd 本蟹住在角落：Sael 回复时它疯狂打字，深夜会睡觉，戳它有惊喜。',
    status: 'ready',
    default: true,
  },
  {
    id: 'accent_glow',
    label: '强调色·光晕',
    desc: '按钮、选中处周围那圈柔光。想要干净利落就关掉。',
    status: 'ready',
    default: true,
  },
  /* —— TG 同款聊天感三件套（2026-07-19 铃点单）—— */
  {
    id: 'split_bubbles',
    label: '他会连发小气泡',
    desc: 'Sael 的一条回复拆成几条相连的小气泡，像在 TG 里连发消息的节奏。',
    status: 'ready',
    default: true,
  },
  {
    id: 'quote_reply',
    label: '引用回复',
    desc: '点消息角落的引号，引用那句话来回应；被引用的内容会显示在气泡里。',
    status: 'ready',
    default: true,
  },
  {
    id: 'sael_reactions',
    label: '他的小心情',
    desc: 'Sael 有时不说话，只在你的消息上贴一个小表情（❤ 🥺 …），像 TG 里那样。',
    status: 'ready',
    default: true,
  },
];

const _defaults = Object.fromEntries(FLAGS.map((f) => [f.id, !!f.default]));
const _listeners = new Set();

export function getFlag(id) {
  try {
    const v = localStorage.getItem(PREFIX + id);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {}
  return _defaults[id] ?? false;
}

export function setFlag(id, on) {
  on = !!on;
  try {
    localStorage.setItem(PREFIX + id, on ? '1' : '0');
  } catch {}
  applyFlag(id, on);
  _listeners.forEach((fn) => {
    try {
      fn(id, on);
    } catch {}
  });
}

/* 订阅开关变化（返回取消订阅函数）。 */
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/* 把「需要在 <html> 上挂属性才生效」的开关同步到 DOM。
   新增这类开关时在这里加一条即可。 */
function applyFlag(id, on) {
  if (id === 'reduce_motion') {
    document.documentElement.toggleAttribute('data-reduce-motion', on);
  }
  if (id === 'accent_shades') {
    // 关 = 挂 data-accent-flat（tokens.css 里三档同色）
    document.documentElement.toggleAttribute('data-accent-flat', !on);
  }
  if (id === 'accent_glow') {
    // 关 = 挂 data-glow-off（tokens.css / Chat.css 里柔光熄灯）
    document.documentElement.toggleAttribute('data-glow-off', !on);
  }
  if (id === 'hide_avatar') {
    // 开 = 挂 data-hide-avatar（Chat.css 里把 .ch-avatar 藏起来）
    document.documentElement.toggleAttribute('data-hide-avatar', on);
  }
}

/* 启动时（main.jsx，首帧前）把所有开关的当前值同步到 DOM，避免刷新闪一下。 */
export function applyAllFlags() {
  FLAGS.forEach((f) => applyFlag(f.id, getFlag(f.id)));
}

/* React hook：[on, toggle]。组件用它读 / 改某个开关，跨组件自动同步。 */
export function useFlag(id) {
  const [on, setOn] = useState(() => getFlag(id));
  useEffect(
    () =>
      subscribe((cid) => {
        if (cid === id) setOn(getFlag(id));
      }),
    [id],
  );
  return [on, (v) => setFlag(id, v)];
}
